import { useTranslation } from 'react-i18next';
import { Bike, MapPin, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { CartView, SavedAddress } from '@/lib/draft-order-client';
import type { DraftWorkingCopy } from './cart-draft';

export interface CartBranch {
  id: string;
  name: string;
}

interface CartDeliverySectionProps {
  copy: DraftWorkingCopy;
  cart: CartView;
  savedAddresses: SavedAddress[];
  /** Branches this session may place for — already scoped, so a sucursal session gets exactly its own. */
  branches: CartBranch[];
  editable: boolean;
  onChange: (patch: Partial<DraftWorkingCopy>) => void;
}

export function CartDeliverySection({
  copy,
  cart,
  savedAddresses,
  branches,
  editable,
  onChange,
}: CartDeliverySectionProps) {
  const { t } = useTranslation('conversations');
  const isDelivery = copy.deliveryType !== 'pickup';

  /**
   * The branch picker only ever appears for a cart with none, and only when there is a choice to make:
   * with a single branch (a one-sucursal brand, or a sucursal-scoped session) the server pins it on
   * the first save, so offering a one-option dropdown would just be a step to click through.
   *
   * Once pinned it renders disabled rather than disappearing — the operator should be able to see
   * which branch the order is for, and see that it is not theirs to change.
   */
  const pinned = Boolean(cart.locationId);
  const showBranchPicker = pinned
    ? // A branch the list does not carry (renamed, removed, or simply not loaded) would render as an
      // empty dropdown; the plain line below still names it.
      branches.some((branch) => branch.id === cart.locationId)
    : branches.length > 1;

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('cart.delivery.title')}
      </h3>

      <div className="grid grid-cols-2 gap-2">
        {(['delivery', 'pickup'] as const).map((type) => {
          const Icon = type === 'delivery' ? Bike : Store;
          const active = copy.deliveryType === type;
          return (
            <button
              key={type}
              type="button"
              disabled={!editable}
              onClick={() => onChange({ deliveryType: type })}
              className={cn(
                'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-background text-muted-foreground',
                editable && !active && 'hover:bg-accent hover:text-foreground',
                !editable && 'cursor-default',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {t(`cart.delivery.${type}`)}
            </button>
          );
        })}
      </div>

      {showBranchPicker ? (
        <div className="space-y-1.5">
          <Label htmlFor="cart-branch" className="text-sm font-medium">
            {t('cart.delivery.branchLabel')}
          </Label>
          <Select
            value={copy.locationId ?? ''}
            disabled={pinned || !editable}
            onValueChange={(locationId) => onChange({ locationId })}
          >
            <SelectTrigger id="cart-branch" className="w-full" aria-label={t('cart.delivery.branchLabel')}>
              <SelectValue placeholder={t('cart.delivery.branchPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {pinned
              ? t('cart.delivery.branchLocked')
              : // Unsettled: name the state before the rule, so the picker reads as the answer to a
                // cart that is genuinely branch-less rather than as an empty required field.
                `${t('cart.delivery.branchPending')} ${t('cart.delivery.branchHint')}`}
          </p>
        </div>
      ) : cart.locationName ? (
        <p className="text-xs text-muted-foreground">{t('cart.delivery.branch', { name: cart.locationName })}</p>
      ) : (
        !cart.locationId && (
          // A shared-menu brand builds the cart before the branch is settled: the panel says so
          // rather than leaving the line blank, which reads as "no branch needed".
          <p className="text-xs text-muted-foreground">{t('cart.delivery.branchPending')}</p>
        )
      )}

      {isDelivery && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cart-address" className="text-sm font-medium">
              {t('cart.delivery.address')}
            </Label>
            <Input
              id="cart-address"
              value={copy.address}
              disabled={!editable}
              onChange={(event) => onChange({ address: event.target.value, savedAddressId: undefined })}
              placeholder={t('cart.delivery.addressPlaceholder')}
            />
            {cart.deliveryAddressConfirmed && copy.address.trim() === (cart.deliveryAddress?.raw ?? '').trim() && (
              <p className="text-xs text-muted-foreground">{t('cart.delivery.confirmed')}</p>
            )}
          </div>

          {editable && savedAddresses.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {savedAddresses.map((address) => (
                <Button
                  key={address.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onChange({ savedAddressId: address.id, address: address.raw })}
                >
                  <MapPin className="h-3 w-3" />
                  {address.label || address.raw}
                </Button>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cart-instructions" className="text-sm font-medium">
              {t('cart.delivery.instructions')}
            </Label>
            <Input
              id="cart-instructions"
              value={copy.instructions}
              disabled={!editable}
              onChange={(event) => onChange({ instructions: event.target.value })}
              placeholder={t('cart.delivery.instructionsPlaceholder')}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cart-fee" className="text-sm font-medium">
              {t('cart.delivery.fee')}
            </Label>
            <Input
              id="cart-fee"
              inputMode="decimal"
              value={copy.deliveryFeeOverride}
              disabled={!editable}
              onChange={(event) => onChange({ deliveryFeeOverride: event.target.value })}
              placeholder={
                cart.deliveryFee != null
                  ? `${cart.currencySymbol}${cart.deliveryFee.toFixed(2)}`
                  : t('cart.delivery.feeAuto')
              }
            />
            {/* The override is the operator's answer to a zone that refuses an address they have
                decided to serve anyway — worth saying out loud, because it also skips the check. */}
            <p className="text-xs text-muted-foreground">
              {cart.deliveryFeePending ? t('cart.delivery.feePending') : t('cart.delivery.feeHint')}
            </p>
            {cart.deliveryAddress?.deliveryZoneName && (
              <p className="text-xs text-muted-foreground">
                {t('cart.delivery.zone', { name: cart.deliveryAddress.deliveryZoneName })}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
